import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, TableProps } from "@mantine/core";
import { CurrencyCode, MyOrder, OrderTabs, VisibleColumnSpec } from "@/types";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useGetMyOrderList, prefetchMyOrderList, prefetchOrderById } from "@/services/Orders";
import { prefetchContractById } from "@/services/Contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import { useDebouncedPrefetch } from "@/hooks/useDebouncedPrefetch";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Tag from "@/components/atoms/Tag";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import SortableHeader from "@/components/molecules/SortableHeader";
import DataTable from "@/components/molecules/DataTable";
import Pagination from "@/components/organisms/Pagination";
import { useAuth } from "@/contexts/AuthContext";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useNavigate } from "react-router-dom";
import { getTradingPartnerCompany } from "@/utils/Contracts";
import { useOrdersDrawerStore } from "@/stores/ordersDrawerStore";
dayjs.extend(advancedFormat);

interface MyOrdersTableProps extends TableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<MyOrder, "actions" | "icon" | "product" | "lastPrice" | "counterParty">[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
}

/**
 * A reusable task table component, with sorting, filtering, actions and pagination.
 * @param TaskTableProps - Component props
 * @param visibleColumns - Constant Array defined to manage which columns to display and when
 * @param initialSort - Define a predefined sort order
 * @param pagination - Boolean to enable or disable pagination
 * @param initialRowsPerPage - set maximum number of rows to display used for snapshots
 *
 **/
const MyOrdersTable: FC<MyOrdersTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination = true,
  actionable: _actionable = true,
  initialRowsPerPage = 15,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const schedulePrefetch = useDebouncedPrefetch();
  const { currentCompany, user } = useAuth();
  const [orderInDrawer, setOrderInDrawer] = useState<number | null>(null);
  const [selectedTab, setSelectedTab] = useState<OrderTabs>("details");
  const openOrdersDrawer = useOrdersDrawerStore((s) => s.open);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: orders, isLoading } = useGetMyOrderList(
    currentCompany?.companyId as number,
    true,
    currentPage,
    false,
    user?.language.id,
    {
      enabled: !!currentCompany?.companyId,
    }
  );

  useEffect(() => {
    if (!orders || !currentCompany?.companyId) return;
    if (currentPage < orders.numberOfPages) {
      prefetchMyOrderList(queryClient, currentCompany.companyId, true, currentPage + 1, false, user?.language.id);
    }
  }, [orders, currentPage, currentCompany?.companyId, queryClient, user?.language.id]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const openDrawerHandler = (orderId: number, tab: OrderTabs) => {
    openOrdersDrawer({ orderId, tab });
  };

  const baseColumns: ColumnDef<MyOrder>[] = useMemo(
    () => [
      {
        header: () => <></>,
        id: "icon",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          if (row.original.status === "REQUIRES_ACTION" || row.original.status === "PENDING_TRANSPORT_SELECTION") {
            return (
              <Group align={"center"} justify={"center"}>
                <SVGAsset name="yellow-exclamation" />
              </Group>
            );
          } else if (row.original.orderType.type === "BUY") {
            return (
              <Group align={"center"} justify={"center"}>
                <SVGAsset name={"purchaseIndicator"} style={{ width: "1.5rem" }} />
              </Group>
            );
          } else if (row.original.orderType.type === "SELL") {
            return (
              <Group align={"center"} justify={"center"}>
                <SVGAsset name={"saleIndicator"} style={{ width: "1.5rem" }} />
              </Group>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:id")} />,
        id: "id",
        accessorKey: "id",
        cell: ({ row }) => {
          return row.original.id;
        },
        size: 30,
        minSize: 30,
        maxSize: 30,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:date")} />,
        id: "created",
        accessorKey: "created",
        cell: ({ row }) => {
          return dayjs(row.original.created).format("Do MMMM YYYY, HH:mm");
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader column={column} label={`${t("common:type")} / ${t("product:product")}`} />
        ),
        id: "salesItem",
        accessorKey: "salesItem.salesItem",
        cell: ({ row }) => {
          const product = row.original.contract?.contractProduct?.companySpecificProduct?.companySpecificProductName;
          if (product) {
            return product;
          } else {
            return <>{t("enumerables:plastic." + row.original.salesItem.salesItem.toLowerCase())}</>;
          }
        },
      },
      {
        header: ({ column }) => <SortableHeader ta="right" column={column} label={t("common:amount")} />,
        id: "amount",
        accessorKey: "amount",
        cell: ({ row }) => {
          const amountDeviation = row.original.amountDeviation ?? 0;
          return (
            <Text ta="right">
              <NumberFormatter value={row.original.amount} suffix={` ${row.original.unit.unit}`} />
              {amountDeviation > 0 ? ` +/- ${amountDeviation}%` : null}
            </Text>
          );
        },
      },
      {
        header: ({ column }) => <SortableHeader ta="right" column={column} label={t("tasks:counterParty")} />,
        id: "counterParty",
        accessorKey: "counterParty",
        cell: ({ row }) => {
          const counterpartyCompany = row.original.contract
            ? getTradingPartnerCompany(row.original.contract, currentCompany?.companyId)
            : null;
          return <Text ta="right">{counterpartyCompany?.companyName}</Text>;
        },
      },
      {
        header: ({ column }) => <SortableHeader ta="right" column={column} label={t("orders:finalAmount")} />,
        id: "finalAmount",
        accessorKey: "finalAmount",
        cell: ({ row }) => {
          const amountDeviation = row.original.amountDeviation ?? 0;
          const finalAmountString =
            row.original.finalAmount && row.original.finalAmount > 0 && amountDeviation > 0
              ? row.original.status === "AWAITING_FINAL_AMOUNT"
                ? "-"
                : `${row.original.finalAmount} ${row.original.unit.unit}`
              : "-";
          return (
            <Text ta="right" truncate={true}>
              {finalAmountString}
            </Text>
          );
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("dashboard:status")} />,
        id: "status",
        accessorKey: "status",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          return (
            <Tag status={row.original.status}>{t("enumerables:orderStatus." + row.original.status.toUpperCase())}</Tag>
          );
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("orders:lastPrice")} />,
        id: "lastPrice",
        accessorFn: (row) => {
          const val = row.price?.[currentCompany?.companyCurrency as CurrencyCode];
          return val ? Number(val) : 0;
        },
        cell: ({ row }) => {
          const lastPrice = row.original.price?.[currentCompany?.companyCurrency as CurrencyCode];
          return (
            <>
              {lastPrice && lastPrice > 0 ? (
                <NumberFormatter
                  value={lastPrice.toFixed(2)}
                  suffix={` ${currentCompany?.companyCurrency}/${row.original.priceUnit?.unit}`}
                />
              ) : (
                "-"
              )}
            </>
          );
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader column={column} label={`${t("common:price")} (${currentCompany?.companyCurrency})`} />
        ),
        id: `totalPriceWithFee.${currentCompany?.companyCurrency}`,
      },
      {
        header: () => <></>,
        id: "actions",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          return (
            row.original.orderType.id === OrderTypeEnum.BUY && (
              <Group justify={"flex-end"} w={"100%"}>
                <Menu offset={0}>
                  <Menu.Target>
                    <span style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                      <Group align={"center"} justify={"center"}>
                        <SVGAsset name="dotdotdot-grey" />
                      </Group>
                    </span>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {row.original.contract ? (
                      <Menu.Item
                        style={{ width: "auto" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/contracts/details/" + row.original.contract?.id);
                        }}
                      >
                        <Group>{t("actions:viewContract")}</Group>
                      </Menu.Item>
                    ) : null}
                    <Menu.Item
                      style={{ width: "auto" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDrawerHandler(row.original.id, "claims");
                      }}
                    >
                      <Group>
                        <SVGAsset name="yellow-exclamation" />
                        {t("orders:reportProblem")}
                      </Group>
                    </Menu.Item>
                    <Menu.Item
                      style={{ width: "auto" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDrawerHandler(row.original.id, "deviations");
                      }}
                    >
                      <Group>
                        <SVGAsset name="yellow-exclamation" />
                        {t("contracts:contractDeviation.deviation")}
                      </Group>
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )
          );
        },
      },
    ],
    [currentCompany?.companyId, t]
  );

  const columns = useVisibleColumns<MyOrder, "actions" | "icon" | "product" | "lastPrice" | "counterParty">(
    baseColumns,
    visibleColumns
  );

  return (
    <>
      <DataTable
        data={orders?.orders || []}
        columns={columns}
        isLoading={isLoading}
        mobileHeaderCell={1}
        showPagination={false}
        onRowMouseEnter={(row) => {
          if (isLoading) return;
          schedulePrefetch(() => {
            prefetchOrderById(queryClient, row.original.id, undefined, user?.language.id);
            if (row.original.contractId) prefetchContractById(queryClient, row.original.contractId);
          });
        }}
        onRowClick={(row) => openOrdersDrawer({ orderId: row.original.id })}
        initialSort={initialSort ?? undefined}
        initialRowsPerPage={initialRowsPerPage}
      />
      {pagination && (
        <Pagination numberOfPages={orders?.numberOfPages || 1} neighbours={1} onClick={handlePageChange} />
      )}
    </>
  );
};

export default MyOrdersTable;
